import { DEFAULT_PAGINATION } from '@/commons/constants';
import { Dispatch } from '@/redux/store';
import { PageRequest, PaginationType, RootModel, ShowroomModelType, ShowroomType } from '@/types';
import { createModel } from '@rematch/core';
import { message } from 'antd';
import produce from 'immer';

const initialState: ShowroomModelType = {
  ids: [],
  loading: false,
  editId: null,
  pagination: DEFAULT_PAGINATION,
  criteria: {
    keyword: '',
  },
};

export const showrooms = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setIds: produce((state, payload) => {
      state.ids = payload;
    }),
    setPagination: produce((state, payload) => {
      state.pagination = payload;
    }),
    setEditId: produce((state, payload) => {
      state.editId = payload;
    }),
    updateCriteria: produce((state, payload) => {
      state.criteria = payload;
    }),
    updateLoading: produce((state, payload) => {
      state.loading = payload;
    }),
  },
  effects: (dispatch: Dispatch) => ({
    async reloadList(payload, state): Promise<void> {
      const { criteria, pagination } = state.showrooms;
      await this.filterAsync({ ...(criteria as object), ...(pagination as object) });
    },

    async filterAsync(payload: any): Promise<void> {
      this.updateLoading(true);
      try {
        let readyPayload = payload;
        if (readyPayload && readyPayload.sort) {
          const clonedPayload = { ...readyPayload };
          const { sort } = clonedPayload;
          if (sort) {
            clonedPayload.sort = sort.map((i) => `${i.property},${i.direction}`).join(',');
          }
          readyPayload = clonedPayload;
        }
        const data: { content: ShowroomType[] } & PaginationType =
          await dispatch.showroomEntities.load(readyPayload);
        if (data && data.content) {
          this.setIds(data.content.map((scene) => scene.id));
          this.setPagination((({ content, pageable, ...otherProps }) => otherProps)(data));
        }
        this.updateCriteria(payload);
      } catch (e) {
        console.error(e);
        message.error('Failed to filter');
      } finally {
        this.updateLoading(false);
      }
    },

    async pagingAsync(payload: PageRequest, state): Promise<void> {
      const { criteria = {}, pagination = {} } = state.showrooms;
      await this.filterAsync({ ...criteria, ...pagination, ...payload });
    },

    async getAsync(payload, state): Promise<void> {
      this.updateLoading(true);
      try {
        const item = await dispatch.showroomEntities.findById(payload);
        this.setEditId(item.id);
        this.updateLoading(false);
      } catch (e) {
        console.error(e);
        message.error(`Failed to get ${payload}`);
      } finally {
        this.updateLoading(false);
      }
    },

    async deleteAsync(payload, state): Promise<void> {
      this.updateLoading(true);
      try {
        await dispatch.showroomEntities.delete(payload);
        await this.reloadList();
        message.warning(`Item deleted`);
      } catch (e) {
        console.error(e);
        message.error(`Failed to delete`);
      } finally {
        this.updateLoading(false);
      }
    },

    async saveAsync(payload, state): Promise<void> {
      this.updateLoading(true);
      try {
        const result = await dispatch.showroomEntities.save(payload);
        message.success('Save successfully');
        return result;
      } catch (err) {
        console.error(err);
        message.error('Fail to save');
      } finally {
        this.updateLoading(false);
      }
    },
    async publish(payload, state) {
      this.updateLoading(true);
      try {
        const item = await dispatch.showroomEntities.publish(payload);
        this.setEditId(item.id);
        this.updateLoading(false);
        message.success(`Success to ${payload.published ? 'published' : 'unpublished'}`);
      } catch (e) {
        console.error(e);
        message.error(`Fail to ${payload.published ? 'published' : 'unpublished'}`);
      } finally {
        this.updateLoading(false);
      }
    },
    async saveAndPublishAsync(payload, state) {
      this.updateLoading(true);
      try {
        const result = await dispatch.showroomEntities.save(payload);
        const res = await dispatch.showroomEntities.publish({ id: result.id, published: true });
        message.success('Save and published successfully');
        return res;
      } catch (err) {
        console.error(err);
        message.error('Fail to save and published');
      } finally {
        this.updateLoading(false);
      }
    },
    async mapVisualizations(payload, state) {
      this.updateLoading(true);
      try {
        const item = await dispatch.showroomEntities.mapVisualizations(payload);
        this.setEditId(item.id);
        this.updateLoading(false);
        message.success('Success to map visualizations');
      } catch (e) {
        console.error(e);
        message.error('Failed to map visualizations');
      } finally {
        this.updateLoading(false);
      }
    },
    async mapProducts(payload, state) {
      this.updateLoading(true);
      try {
        const item = await dispatch.showroomEntities.mapProducts(payload);
        this.setEditId(item.id);
        this.updateLoading(false);
        message.success('Success to map products');
      } catch (e) {
        console.error(e);
        message.error('Failed to map products');
      } finally {
        this.updateLoading(false);
      }
    },
    async updateDefaultShowroom(payload, state) {
      this.updateLoading(true);
      try {
        const item = await dispatch.showroomEntities.updateDefaultShowroom(payload);
        this.setEditId(item.id);
        this.updateLoading(false);
        message.success('Success to update default showroom');
      } catch (e) {
        console.error(e);
        message.error('Failed to update default showroom');
      } finally {
        this.updateLoading(false);
      }
    },
  }),
});
